<template>
  <Authenticated>
    <TrainingNav />
    <div class="roles-container">
      <div class="roles-header">
        <h2>Roles</h2>
        <button @click="addRole = !addRole" class="btn-create">
          <font-awesome-icon :icon="addRole ? 'fa-xmark' : 'fa-plus'" />
          {{ addRole ? "Cancel" : "Create Role" }}
        </button>
      </div>

      <div v-if="addRole" class="add-role-form">
        <input type="text" v-model="newRoleName" placeholder="Enter role name" @keyup.enter="createRole" />
        <div class="actions">
          <div class="input">
            <label for="applyToAllStaff">Apply to all staff</label>
            <input type="checkbox" id="applyToAllStaff" v-model="applyToAllStaff" />
          </div>
          <button @click="createRole" class="btn-save">Save Role</button>
        </div>
      </div>

      <div v-if="getRoles.length > 0" class="roles-grid">
        <div v-for="role in getRoles" :key="role" class="role-card" :class="{ expanded: expandedRole === role }" @click="toggleRoleOptions(role)">
          <div class="role-main">
            <span class="role-icon"><font-awesome-icon icon="fa-user-tag" /></span>
            <span class="role-name">{{ role }}</span>
            <font-awesome-icon class="expand-icon" :icon="expandedRole === role ? 'fa-chevron-up' : 'fa-chevron-down'" />
          </div>
          <div v-if="expandedRole === role" class="role-actions">
            <button class="btn-apply-all" @click.stop="applyRoleToAll(role)">Apply to all staff</button>
          </div>
        </div>
      </div>
      <div v-else class="empty-state">
        <font-awesome-icon icon="fa-user-tag" size="2x" />
        <p>No roles created yet</p>
      </div>
    </div>
  </Authenticated>
</template>

<script>
import Authenticated from "../_layouts/Authenticated.vue";
import TrainingNav from "./TrainingNav.vue";
import { mapGetters } from "vuex";

export default {
  name: "Roles",
  components: {
    Authenticated,
    TrainingNav,
  },
  computed: {
    ...mapGetters("training", ["getRoles"]),
  },
  data() {
    return {
      addRole: false,
      newRoleName: "",
      applyToAllStaff: false,
      expandedRole: null,
    };
  },
  methods: {
    toggleRoleOptions(role) {
      this.expandedRole = this.expandedRole === role ? null : role;
    },

    applyRoleToAll(role) {
      this.$store.dispatch("training/applyRoleToAllStaff", role);
      this.expandedRole = null;
    },

    createRole() {
      const roleName = this.newRoleName.trim();
      if (!roleName) return;
      this.$store.dispatch("training/createRole", { name: roleName, applyToAllStaff: this.applyToAllStaff });
      this.newRoleName = "";
      this.applyToAllStaff = false;
      this.addRole = false;
    },
  },
  mounted() {},
};
</script>

<style scoped lang="scss">
.roles-container {
  margin: 1rem;
  padding: 1.5rem;
  border-radius: 0.5rem;
  box-shadow: var(--box-shadow);
  background-color: var(--primary-colour);

  .roles-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    h2 {
      margin: 0;
      color: var(--text-colour);
    }
  }

  .btn-create {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.6rem 1.2rem;
    border-radius: 0.3rem;
    background-color: var(--action-colour);
    color: var(--primary-colour);
    border: 1px solid var(--action-colour);
    cursor: pointer;
    font-weight: 600;
    transition: opacity 0.2s ease;
    &:hover {
      opacity: 0.85;
    }
  }

  .add-role-form {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    margin-bottom: 1.5rem;
    padding: 1rem;
    background-color: var(--secondary-colour);
    border-radius: 0.5rem;

    .actions {
      display: flex;
      align-items: center;
      gap: 0.5rem;
        .input {
          display: flex;
          align-items: center;
          flex-direction: column;
          gap: 0.5rem;
        }
    }

    input {
      flex: 1;
      padding: 0.6rem 0.75rem;
      border-radius: 0.3rem;
      border: 1px solid var(--border-colour);
      background-color: var(--primary-colour);
      color: var(--text-colour);
      font-size: 0.95rem;
      &:focus {
        outline: none;
        border-color: var(--action-colour);
      }
    }

    .btn-save {
      padding: 0.6rem 1.2rem;
      border-radius: 0.3rem;
      background-color: var(--success-colour);
      color: var(--primary-colour);
      border: 1px solid var(--success-colour);
      cursor: pointer;
      font-weight: 600;
      transition: opacity 0.2s ease;
      &:hover {
        opacity: 0.85;
      }
    }
  }

  .roles-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 1rem;

    .role-card {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      padding: 1rem;
      background-color: var(--secondary-colour);
      border-radius: 0.5rem;
      box-shadow: var(--box-shadow);
      border-left: 3px solid var(--action-colour);
      transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
      cursor: pointer;

      &.expanded {
        border-left-color: var(--success-colour);
      }

      .role-main {
        display: flex;
        align-items: center;
        gap: 0.75rem;
      }

      .role-icon {
        width: 2rem;
        height: 2rem;
        border-radius: 50%;
        background-color: var(--opacity-background);
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--action-colour);
        flex-shrink: 0;
      }

      .role-name {
        font-weight: 600;
        text-transform: capitalize;
        color: var(--text-colour);
        font-size: 0.95rem;
        flex: 1;
      }

      .expand-icon {
        color: var(--action-colour);
        font-size: 0.8rem;
        opacity: 0.8;
      }

      .role-actions {
        margin-top: 0.7rem;
        padding: 0.7rem 0.25rem;
        border-top: 1px solid var(--border-colour);

        .btn-apply-all {
          width: 100%;
          border: 1px solid var(--action-colour);
          background-color: transparent;
          color: var(--action-colour);
          border-radius: 0.35rem;
          padding: 0.45rem 0.6rem;
          cursor: pointer;
          font-weight: 600;

          &:hover {
            background-color: var(--action-colour);
            color: var(--primary-colour);
          }
        }
      }
    }
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    color: var(--text-colour);
    opacity: 0.4;
    gap: 0.75rem;
    p {
      margin: 0;
      font-size: 1rem;
    }
  }
}
</style>
